import { inject, Injectable } from '@angular/core';
import { CurrentUser, CurrentUserContext, CurrentUserService } from '@rma/generic/ng/user';
import { map } from 'rxjs';

/**
 * UserContextService
 *
 * Maps the current user from {@link CurrentUserService} to a simplified {@link CurrentUserContext} object.
 *
 * This service provides an observable `currentUserContext$` that emits the mapped context or `null` if no user is present.
 * It is intended as a lightweight abstraction for consuming user context in components and services.
 *
 * @deprecated Please user the {@link DashboardUserContextService} instead of this.
 *
 * @remarks
 * - This service is a thin wrapper and may be removed in the future in favor of using `CurrentUser` directly.
 */
@Injectable({
  providedIn: 'root',
})
export class UserContextService {
  private readonly currentUserService = inject(CurrentUserService);

  public readonly currentUserContext$ = this.currentUserService.currentUser$.pipe(map((user) => this.mapUserToCurrentUserContext(user)));

  private mapUserToCurrentUserContext(user: CurrentUser | null): CurrentUserContext | null {
    if (!user) {
      return null;
    }

    return {
      alias: user.alias,
      agents: user.agents,
      email: user.email,
      firstname: user.firstname,
      hasAssistantAccess: user.hasAssistantAccess,
      hasMultipleProfiles: user.hasMultipleProfiles,
      hasSeenWelcomeModal: user.hasSeenWelcomeModal,
      isImpersonating: user.isImpersonating,
      lastLoggedIn: user.lastLoggedIn,
      lastname: user.lastname,
      name: user.name ?? user.alias,
      timeZoneId: user.timeZoneId,
      userId: user.userId,
      userType: user.userType,
    };
  }
}
